import { SafePromise } from '@benzinga/safe-await';
import { Subscribable, Subscription } from '@benzinga/subscribable';
import { AuthenticationManager, Session } from '@benzinga/session';

import { UserRequest, UserRestfulEvent } from './request';
import { UserStore, UserStoreEvent } from './store';
import { UserSettingsFeed } from './feed';
import { UserSettingConfig } from './entities';
import { oneRequestAtATime } from '@benzinga/utils';

export type UserSettingsManagerEvent = UserRestfulEvent | UserStoreEvent;

export class UserSettingsManager extends Subscribable<UserSettingsManagerEvent> {
  private store: UserStore;
  private request: UserRequest;
  private session: Session;
  private feeds: Map<string, UserSettingsFeed>;
  private requestSubscription?: Subscription<UserRequest>;
  private storeSubscription?: Subscription<UserStore>;

  private getDelayedUserSettings = oneRequestAtATime(async () => {
    const cachedSettings = this.store.getUserSettings();
    if (cachedSettings && Object.keys(cachedSettings).length > 0) return { ok: cachedSettings };
    const res = await this.request.getUserSettings();
    if (res.ok) {
      this.store.setUserSettings(res.ok);
    }
    return res;
  });
  constructor(session: Session) {
    super();
    this.request = new UserRequest(session);
    this.store = new UserStore();
    this.session = session;
    this.feeds = new Map();
    this.fetchUserSettings();

    const authManagerSubscription = session.getManager(AuthenticationManager).subscribe(event => {
      switch (event.type) {
        case 'authentication:session_update':
          this.fetchUserSettings();
          break;
      }
    });
    session.onPreSessionClose(() => authManagerSubscription.unsubscribe());
    if (session.getManager(AuthenticationManager).isLoggedIn()) {
      this.fetchUserSettings();
    }
  }

  public static getName = () => 'benzinga-user-settings';

  public fetchUserSettings = async (force?: boolean): SafePromise<Record<string, object>> => {
    if (this.store.shouldUpdateUserSettings() || force) {
      return this.getDelayedUserSettings();
    }
    const globalSettings = this.store.getUserSettings();
    if (!globalSettings) {
      throw new Error('User settings not found in store');
    }
    return { ok: globalSettings };
  };

  public getUserSettings = (): Record<string, object> | undefined => {
    return this.store.getUserSettings();
  };

  public setUserSetting = (key: string, value: object) => {
    this.store.setUserSettings({
      ...this.store.getUserSettings(),
      [key]: value,
    });
    this.request.setUserSettings(key, value);
  };

  public flushPendingSettings = () => {
    this.request.flushUserSettings();
  };

  public getFeed = <KEY extends string, VER extends number, VAL extends object, ALL_VAL extends object>(
    config: UserSettingConfig<KEY, VER, VAL, ALL_VAL>,
  ): UserSettingsFeed<KEY, VER, VAL, ALL_VAL> => {
    if (this.feeds.has(config.key)) {
      return this.feeds.get(config.key) as unknown as UserSettingsFeed<KEY, VER, VAL, ALL_VAL>;
    } else {
      const feed = new UserSettingsFeed(config, this.session, this);
      this.feeds.set(config.key, feed as unknown as UserSettingsFeed);
      return feed;
    }
  };

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.subscribe(event => {
      this.dispatch(event);
    });
    this.storeSubscription = this.store.subscribe(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
    this.requestSubscription = undefined;
    this.storeSubscription?.unsubscribe();
    this.storeSubscription = undefined;
  }
}
